import { Form } from "react-bootstrap";
import { Switch } from "@mui/material";
import CitySettingsModal from "components/Cities/CitySettingsModal/CitySettingsModal";
import FieldHelp from "components/Ui/FieldHelp/FieldHelp";
import TimeField from "components/Ui/TimeField/TimeField";
import { parseSeatsForm, seatsFormValues, WEEKDAYS, WEEKDAY_LABELS } from "utils/driverShiftWeek";

/**
 * The form values of a shift. The page fills them in the click that opens the
 * form, so the modal (which must stay mounted for its open animation) needs no
 * effect to reset them.
 *
 * @param {object|null} template
 */
export const shiftFormFrom = (template) => ({
    name: template?.name ?? "",
    startTime: template?.start_time ?? "10:00",
    endTime: template?.end_time ?? "17:00",
    seats: seatsFormValues(template?.seats_by_weekday),
    active: template ? template.active : true,
    error: "",
});

/**
 * Create or edit one shift of a city (admin#74).
 *
 * The shell is the city settings modal: the same header, form, buttons and
 * z-index above the sidebar. A shift whose end is not after its start goes
 * past midnight; the form says so instead of refusing it.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {() => void} props.close
 * @param {{id: number, name: string}|null} props.city
 * @param {object|null} props.template - the shift to edit, or null for a new one
 * @param {ReturnType<typeof shiftFormFrom>} props.form
 * @param {(form: ReturnType<typeof shiftFormFrom>) => void} props.setForm
 * @param {boolean} props.saving
 * @param {(values: object) => Promise<void>} props.onSave
 */
const ShiftTemplateModal = ({ show, close, city, template, form, setForm, saving, onSave }) => {
    const { name, startTime, endTime, seats, active } = form;
    const set = (field) => (value) => setForm({ ...form, [field]: value });
    const setName = set("name");
    const setStartTime = set("startTime");
    const setEndTime = set("endTime");
    const setSeats = set("seats");
    const setActive = set("active");
    const setFormError = set("error");
    const formError = form.error;

    const crossesMidnight = startTime && endTime && endTime <= startTime;

    const submit = async (event) => {
        event.preventDefault();
        const seatsByWeekday = parseSeatsForm(seats);
        if (!name.trim()) return setFormError("اكتب اسم الوردية");
        if (!startTime || !endTime) return setFormError("اختر وقت البداية والنهاية");
        if (startTime === endTime)
            return setFormError("بداية الوردية ونهايتها لا يمكن أن تكونا نفس الوقت");
        if (!seatsByWeekday)
            return setFormError("عدد الأماكن يجب أن يكون رقمًا صحيحًا من 0 إلى 500");
        setFormError("");
        await onSave({
            name: name.trim(),
            start_time: startTime,
            end_time: endTime,
            seats_by_weekday: seatsByWeekday,
            ...(template ? { active } : {}),
        });
    };

    return (
        <CitySettingsModal
            show={show}
            close={close}
            region={city}
            title={template ? "تعديل وردية" : "وردية جديدة"}
            loading={saving}
            onSubmit={submit}
            submitLabel={template ? "حفظ الوردية" : "إضافة الوردية"}
        >
            <div className="city-settings-modal__section">
                {formError ? <div className="form-warning">{formError}</div> : null}

                <Form.Group className="mb-3" controlId="shiftName">
                    <Form.Label>اسم الوردية</Form.Label>
                    <Form.Control
                        type="text"
                        maxLength={60}
                        value={name}
                        placeholder="مثلاً: صباحي"
                        onChange={(event) => setName(event.target.value)}
                    />
                </Form.Group>

                <div className="driver-shift-form__times">
                    <Form.Group controlId="shiftStart">
                        <Form.Label>من</Form.Label>
                        <TimeField
                            inputId="shiftStart"
                            className="w-100"
                            value={startTime}
                            onChange={setStartTime}
                        />
                    </Form.Group>
                    <Form.Group controlId="shiftEnd">
                        <Form.Label>إلى</Form.Label>
                        <TimeField
                            inputId="shiftEnd"
                            className="w-100"
                            value={endTime}
                            onChange={setEndTime}
                        />
                    </Form.Group>
                </div>
                {crossesMidnight && startTime !== endTime ? (
                    <div className="driver-shift-form__note">
                        تنتهي هذه الوردية في اليوم التالي.
                    </div>
                ) : null}

                <div className="city-settings-modal__section-title">
                    عدد الأماكن
                    <FieldHelp label="عن عدد الأماكن">
                        عدد السائقين في هذه الوردية لكل يوم من أيام الأسبوع. يمكن تغيير يوم واحد فقط
                        (عطلة مثلاً) من جدول الأسبوع.
                    </FieldHelp>
                </div>
                <div className="driver-shift-form__seats">
                    {WEEKDAYS.map((day) => (
                        <Form.Group key={day} controlId={`seats-${day}`}>
                            <Form.Label>{WEEKDAY_LABELS[day]}</Form.Label>
                            <Form.Control
                                type="number"
                                min={0}
                                max={500}
                                step={1}
                                inputMode="numeric"
                                value={seats[day]}
                                onChange={(event) =>
                                    setSeats({ ...seats, [day]: event.target.value })
                                }
                            />
                        </Form.Group>
                    ))}
                </div>

                {template ? (
                    <Form.Group className="mt-3" controlId="shiftActive">
                        <div className="city-settings-modal__toggle">
                            <Form.Label>الوردية مفعّلة</Form.Label>
                            <FieldHelp label="عن إيقاف الوردية">
                                الوردية الموقوفة تبقى في السجل، لكنها لا تضع أي سائق في الوردية ولا
                                تقبل سائقين جدد.
                            </FieldHelp>
                            <Switch
                                color="warning"
                                size="small"
                                checked={active}
                                onChange={(event) => setActive(event.target.checked)}
                            />
                        </div>
                    </Form.Group>
                ) : null}
            </div>
        </CitySettingsModal>
    );
};

export default ShiftTemplateModal;
